import { useState } from "react";
import { Link, matchPath, useLocation } from "react-router";
import { ListBase, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { DealCreate } from "@/components/atomic-crm/deals/DealCreate";
import { DealEdit } from "@/components/atomic-crm/deals/DealEdit";
import { DealShow } from "@/components/atomic-crm/deals/DealShow";
import type { Deal } from "@/components/atomic-crm/types";
import { nativeSelectClass } from "../tasks/taskData";
import { CLOSED_STAGE_PAGE, stageAmount, type PipelineStage } from "./dealData";
import { LostReasonsDialog } from "./LostReasonsDialog";
import { TenaxNextActionBadge } from "./TenaxNextAction";
import { useAmountFormat } from "./useAmountFormat";
import { ACTIVE_DEALS_FILTER, useDealBoard } from "./useDealBoard";

// Deals on the phone (task 1.7): one pipeline, deals grouped by stage, and
// "Move" then the stage: two taps (a lost stage also asks the reason, an
// open stage the next action when the deal has none).
export const TenaxDealListMobile = () => (
  <ListBase
    resource="deals"
    // the list gives only the filters, the deals come from usePipelineDeals
    perPage={1}
    filter={ACTIVE_DEALS_FILTER}
    sort={{ field: "index", order: "ASC" }}
  >
    <MobileDeals />
  </ListBase>
);

const MobileDeals = () => {
  const translate = useTranslate();
  const location = useLocation();
  const board = useDealBoard();
  const [moving, setMoving] = useState<Deal | null>(null);
  const [isReasonsOpen, setReasonsOpen] = useState(false);
  const matchCreate = matchPath("/deals/create", location.pathname);
  const matchShow = matchPath("/deals/:id/show", location.pathname);
  const matchEdit = matchPath("/deals/:id", location.pathname);

  const moveTo = (stage: PipelineStage) => {
    if (!moving?.stage_id) return;
    const target = board.columns[stage.id] ?? [];
    setMoving(null);
    board.move({
      dealId: moving.id,
      fromStageId: moving.stage_id,
      toStageId: stage.id,
      // on the phone a moved deal goes to the top of its new stage
      beforeId: target.find((deal) => deal.id !== moving.id)?.id ?? null,
    });
  };

  return (
    <>
      <MobileHeader showRefresh>
        <h1 className="text-xl font-semibold">
          {translate("resources.deals.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <select
              aria-label={translate("tenax.deals.pipeline")}
              className={nativeSelectClass}
              value={board.pipelineId ?? ""}
              onChange={(event) =>
                board.setPipeline(Number(event.target.value))
              }
            >
              {board.pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>
            <Button asChild>
              <Link to="/deals/create">{translate("tenax.deals.new")}</Link>
            </Button>
          </div>
          {board.isPending
            ? null
            : board.stages.map((stage) => (
                <StageSection
                  key={stage.id}
                  stage={stage}
                  deals={board.columns[stage.id] ?? []}
                  onMove={setMoving}
                />
              ))}
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => setReasonsOpen(true)}
          >
            {translate("tenax.deals.reasons.title")}
          </Button>
        </div>
      </MobileContent>

      <Sheet open={!!moving} onOpenChange={(open) => !open && setMoving(null)}>
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{translate("tenax.deals.move_to")}</SheetTitle>
            <SheetDescription>{moving?.name}</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-2 px-4 pb-6">
            {board.stages.map((stage) => (
              <Button
                key={stage.id}
                variant={
                  stage.id === moving?.stage_id ? "secondary" : "outline"
                }
                disabled={stage.id === moving?.stage_id}
                className="h-11 justify-start"
                onClick={() => moveTo(stage)}
              >
                {stage.name}
              </Button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {board.moveDialog}
      <LostReasonsDialog open={isReasonsOpen} onOpenChange={setReasonsOpen} />
      <DealCreate open={!!matchCreate} />
      <DealEdit open={!!matchEdit && !matchCreate} id={matchEdit?.params.id} />
      <DealShow open={!!matchShow} id={matchShow?.params.id} />
    </>
  );
};

const StageSection = ({
  stage,
  deals,
  onMove,
}: {
  stage: PipelineStage;
  deals: Deal[];
  onMove: (deal: Deal) => void;
}) => {
  const translate = useTranslate();
  const formatAmount = useAmountFormat();
  const total = stageAmount(deals);
  const isClosed = stage.is_won || stage.is_lost;
  const [shownCount, setShownCount] = useState(CLOSED_STAGE_PAGE);
  const shown = isClosed ? deals.slice(0, shownCount) : deals;
  return (
    <section aria-label={stage.name} className="flex flex-col gap-1">
      <h2 className="text-sm font-semibold text-muted-foreground">
        {stage.name} ({deals.length}
        {total == null ? null : ` · ${formatAmount(total)}`})
      </h2>
      <ul className="flex flex-col gap-1">
        {shown.map((deal) => (
          <li
            key={deal.id}
            aria-label={deal.name}
            className="flex items-center gap-2 rounded-md border px-3 py-2"
          >
            <Link to={`/deals/${deal.id}/show`} className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{deal.name}</div>
              {deal.amount ? (
                <div className="text-xs text-muted-foreground">
                  {formatAmount(deal.amount)}
                </div>
              ) : null}
              <TenaxNextActionBadge deal={deal} />
            </Link>
            <Button variant="outline" size="sm" onClick={() => onMove(deal)}>
              {translate("tenax.deals.move")}
            </Button>
          </li>
        ))}
      </ul>
      {shown.length < deals.length ? (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => setShownCount(shownCount + CLOSED_STAGE_PAGE)}
        >
          {translate("tenax.deals.show_more", {
            smart_count: Math.min(
              CLOSED_STAGE_PAGE,
              deals.length - shown.length,
            ),
            shown: shown.length,
            total: deals.length,
          })}
        </Button>
      ) : null}
    </section>
  );
};
